import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Briefcase,
  TrendingUp,
  Wallet,
  Clock,
  AlertCircle,
  CheckCircle2,
  Users,
  FileText,
  Building2,
  ArrowRight,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';

const STATO_COLORS = {
  Preventivo: 'bg-blue-100 text-blue-800',
  Attiva: 'bg-emerald-100 text-emerald-800',
  'In attesa cliente': 'bg-yellow-100 text-yellow-800',
  'In attesa ente': 'bg-amber-100 text-amber-800',
  Sospesa: 'bg-orange-100 text-orange-800',
  Conclusa: 'bg-gray-100 text-gray-800',
  Archiviata: 'bg-gray-200 text-gray-600',
};

const fmtEur = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function StudioDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const r = await studioApi.dashboard();
      setData(r.data);
    } catch (e) {
      toast.error('Errore caricamento dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) return <p className="p-6 text-gray-500 italic">Caricamento dashboard...</p>;

  const k = data.kpi;
  const kpiCards = [
    { label: 'Commesse attive', value: k.commesse_attive, icon: Briefcase, color: 'from-emerald-500 to-emerald-600', testId: 'kpi-attive' },
    { label: 'Parcelle previste', value: fmtEur(k.parcelle_previste), icon: TrendingUp, color: 'from-blue-500 to-blue-600', testId: 'kpi-parcelle' },
    { label: 'Incassato', value: fmtEur(k.incassato), icon: Wallet, color: 'from-purple-500 to-purple-600', testId: 'kpi-incassato' },
    { label: 'Importo lavori gestito', value: fmtEur(k.importo_lavori_gestito), icon: Building2, color: 'from-cyan-500 to-cyan-600', testId: 'kpi-importo' },
    { label: 'Ore lavorate', value: k.ore_lavorate, icon: Clock, color: 'from-amber-500 to-amber-600', testId: 'kpi-ore' },
    { label: 'Avanzamento medio', value: `${Math.round((k.avanzamento_medio || 0) * 100)}%`, icon: TrendingUp, color: 'from-green-500 to-green-600', testId: 'kpi-avanz' },
    { label: 'Scadenze imminenti (7gg)', value: k.scadenze_imminenti, icon: AlertCircle, color: 'from-yellow-500 to-yellow-600', testId: 'kpi-imm' },
    { label: 'Scadenze SCADUTE', value: k.scadenze_scadute, icon: AlertCircle, color: 'from-red-500 to-red-600', testId: 'kpi-scadute' },
  ];

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="studio-dashboard">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-bold">Dashboard Studio Tecnico</h1>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Direzionale ERP — Commesse, parcelle, scadenze e KPI operativi
        </p>
      </motion.div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`rounded-lg p-3 lg:p-4 text-white shadow-sm bg-gradient-to-br ${card.color}`}
              data-testid={card.testId}
            >
              <div className="flex items-start justify-between">
                <span className="text-xs opacity-90">{card.label}</span>
                <Icon className="w-4 h-4 opacity-80" />
              </div>
              <div className="text-2xl font-bold mt-2">{card.value}</div>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Distribuzione stati */}
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-4"
        >
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            Distribuzione per stato
          </h2>
          <div className="space-y-2">
            {data.distribuzione_stati.filter(d => d.numero > 0).map((d) => (
              <div key={d.stato} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Badge className={STATO_COLORS[d.stato] || 'bg-gray-100 text-gray-800'}>
                    {d.numero}
                  </Badge>
                  <span>{d.stato}</span>
                </div>
                <span className="font-mono text-gray-600">{fmtEur(d.parcelle)}</span>
              </div>
            ))}
            {data.distribuzione_stati.every(d => d.numero === 0) && (
              <p className="text-sm text-gray-500 italic">Nessuna commessa registrata.</p>
            )}
          </div>
        </motion.div>

        {/* Top commesse */}
        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-4"
        >
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
            Top commesse per parcella
          </h2>
          <div className="space-y-2">
            {data.top_commesse.length === 0 && <p className="text-sm text-gray-500 italic">Nessuna commessa attiva.</p>}
            {data.top_commesse.map((c) => (
              <div key={c.codice} className="border rounded p-2.5 text-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-xs text-indigo-700">{c.codice}</div>
                    <div className="font-semibold">{c.cliente_nome}</div>
                    <div className="text-xs text-gray-600">{c.oggetto}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">parcella</div>
                    <div className="font-semibold">{fmtEur(c.parcella_prevista)}</div>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">
                      {Math.round((c.avanzamento || 0) * 100)}%
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Shortcuts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/studio/commesse" className="bg-white rounded border p-3 hover:shadow-md transition flex items-center justify-between" data-testid="link-commesse">
          <div>
            <div className="text-xs text-gray-500">Vai a</div>
            <div className="font-semibold flex items-center gap-1"><Briefcase className="w-4 h-4 text-indigo-600" /> Commesse</div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </Link>
        <Link to="/studio/clienti" className="bg-white rounded border p-3 hover:shadow-md transition flex items-center justify-between" data-testid="link-clienti">
          <div>
            <div className="text-xs text-gray-500">Vai a</div>
            <div className="font-semibold flex items-center gap-1"><Users className="w-4 h-4 text-indigo-600" /> Clienti ({k.tot_clienti})</div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </Link>
        <Link to="/studio/preventivi" className="bg-white rounded border p-3 hover:shadow-md transition flex items-center justify-between" data-testid="link-preventivi">
          <div>
            <div className="text-xs text-gray-500">Vai a</div>
            <div className="font-semibold flex items-center gap-1"><FileText className="w-4 h-4 text-indigo-600" /> Preventivi ({k.tot_preventivi_aperti})</div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </Link>
        <Link to="/studio/scadenze" className="bg-white rounded border p-3 hover:shadow-md transition flex items-center justify-between" data-testid="link-scadenze">
          <div>
            <div className="text-xs text-gray-500">Vai a</div>
            <div className="font-semibold flex items-center gap-1"><AlertCircle className="w-4 h-4 text-indigo-600" /> Scadenze</div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </Link>
      </div>
    </div>
  );
}
